import { useEffect, useState, useMemo, FormEvent, ReactNode } from 'react'
import superagent from 'superagent'
import { useAlert, useAuth } from '../App'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import InputAdornment from '@mui/material/InputAdornment'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PersonSearchIcon from '@mui/icons-material/PersonSearchOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import VoicemailIcon from '@mui/icons-material/VoicemailOutlined'
import SupportAgentIcon from '@mui/icons-material/SupportAgentOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import AskSupportDialog from '../components/AskSupportDialog'
import {
  buildUserSummary,
  getRenewStatus
} from '../utils/userSummary'
import {
  inputSx,
  primaryButtonSx,
  secondaryButtonSx,
  destructiveButtonSx
} from '../styles/authSx'

type Phone = {
  phone: string
  suid: string
  active: boolean
  carrier?: { name?: string }
}

type Mail = { mail: string; suid: string; active: boolean }

type User = {
  voxuid: string
  firstName?: string
  lastName?: string
  language?: string
  plan?: string
  renew?: string | number
  transcriptions?: number
  extraTranscriptions?: number
  creationDate?: string | number
  phoneNumbers?: Phone[]
  mails?: Mail[]
  lastVoicemail?: {
    date?: string | number
    callerNumber?: string
    vmuid?: string
  }
}

type DetectedType = 'voxuid' | 'phone' | 'email'

type RecentSearch = { query: string; type: DetectedType; ts: number }

const RECENTS_KEY = 'voxist:recentSearches'
const RECENTS_MAX = 5

const loadRecents = (): RecentSearch[] => {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.slice(0, RECENTS_MAX)
  } catch {
    return []
  }
}

const apiUrl = (path: string) => path

const PHONE_E164 = /^\+[1-9]\d{7,14}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isValidPhone = (value: string) => PHONE_E164.test(value)
const isValidEmail = (value: string) => EMAIL_RE.test(value)

const detectType = (raw: string): DetectedType => {
  const q = raw.trim()
  if (q.includes('@')) return 'email'
  if (q.startsWith('voxuid-')) return 'voxuid'
  if (q.startsWith('+')) return 'phone'
  if (/^[\d\s\-().]+$/.test(q) && (q.match(/\d/g) || []).length >= 7)
    return 'phone'
  return 'voxuid'
}

const formatDate = (value?: string | number) => {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const relativeFromNow = (value?: string | number) => {
  if (!value) return null
  const d = new Date(value)
  if (isNaN(d.getTime())) return null
  const diffMs = d.getTime() - Date.now()
  const days = Math.round(diffMs / 86_400_000)
  if (days === 0) return 'Today'
  if (days > 0)
    return `In ${days} day${days > 1 ? 's' : ''}`
  return `${-days} day${-days > 1 ? 's' : ''} ago`
}

const initials = (u: User) => {
  const a = (u.firstName || '').trim()[0]
  const b = (u.lastName || '').trim()[0]
  if (a || b) return `${a || ''}${b || ''}`.toUpperCase()
  return (u.voxuid || '?').replace('voxuid-', '').slice(0, 2).toUpperCase()
}

const sectionSx = (isDark: boolean) => ({
  borderRadius: '16px',
  border: `1px solid ${isDark ? '#1F1F22' : '#E5E5EA'}`,
  backgroundColor: isDark ? '#0E0E10' : '#FFFFFF',
  padding: { xs: 2.5, sm: 3 }
})

const sectionHeaderSx = (isDark: boolean) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: isDark ? '#98989D' : '#6E6E73',
  mb: 2
})

const Section = ({
  title,
  icon,
  count,
  children,
  action
}: {
  title: string
  icon?: ReactNode
  count?: number
  children: ReactNode
  action?: ReactNode
}) => (
  <Box sx={(t) => sectionSx(t.palette.mode === 'dark')}>
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={(t) => ({
        pb: 1.75,
        mb: 2,
        borderBottom: `1px solid ${
          t.palette.mode === 'dark' ? '#1F1F22' : '#F2F2F5'
        }`
      })}
    >
      {icon && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '8px',
            backgroundColor:
              t.palette.mode === 'dark'
                ? 'rgba(0, 207, 243, 0.12)'
                : 'rgba(0, 207, 243, 0.10)',
            color: '#00B8DB',
            flexShrink: 0
          })}
        >
          {icon}
        </Box>
      )}
      <Box
        sx={(t) => ({
          fontSize: '0.9375rem',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
          flex: 1
        })}
      >
        {title}
      </Box>
      {count !== undefined && (
        <Box
          sx={(t) => ({
            fontSize: '0.75rem',
            fontWeight: 600,
            color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
            backgroundColor:
              t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5',
            borderRadius: '999px',
            px: 1,
            py: 0.25,
            minWidth: 24,
            textAlign: 'center'
          })}
        >
          {count}
        </Box>
      )}
      {action}
    </Stack>
    {children}
  </Box>
)

const Field = ({
  label,
  value
}: {
  label: string
  value: ReactNode
}) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={(t) => ({
      py: 1.25,
      borderBottom: `1px solid ${
        t.palette.mode === 'dark' ? '#1F1F22' : '#F2F2F5'
      }`,
      '&:last-of-type': { borderBottom: 'none' }
    })}
  >
    <Box
      sx={(t) => ({
        fontSize: '0.875rem',
        color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
      })}
    >
      {label}
    </Box>
    <Box
      sx={(t) => ({
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
        textAlign: 'right',
        wordBreak: 'break-word',
        ml: 2
      })}
    >
      {value ?? '—'}
    </Box>
  </Stack>
)

const StatusChip = ({ active }: { active: boolean }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    size="small"
    sx={(t) => {
      const isDark = t.palette.mode === 'dark'
      return {
        height: 22,
        minWidth: 68,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        backgroundColor: active
          ? isDark
            ? 'rgba(48, 209, 88, 0.18)'
            : 'rgba(48, 209, 88, 0.14)'
          : isDark
          ? 'rgba(255, 69, 58, 0.20)'
          : 'rgba(255, 69, 58, 0.14)',
        color: active
          ? isDark
            ? '#5BE584'
            : '#0F812B'
          : isDark
          ? '#FF6961'
          : '#B82B22',
        border: 'none',
        '& .MuiChip-label': { px: 1 }
      }
    }}
  />
)

const RenewChip = ({ renew }: { renew?: string | number }) => {
  const status = getRenewStatus(renew)
  if (!status) return null
  if (status.kind === 'ok') return null
  const isExpired = status.kind === 'expired'
  const label = isExpired
    ? `Expired ${-status.days}d ago`
    : status.days === 0
    ? 'Renews today'
    : `Renews in ${status.days}d`
  return (
    <Chip
      label={label}
      size="small"
      sx={(t) => {
        const isDark = t.palette.mode === 'dark'
        if (isExpired) {
          return {
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            backgroundColor: isDark
              ? 'rgba(255, 69, 58, 0.20)'
              : 'rgba(255, 69, 58, 0.14)',
            color: isDark ? '#FF6961' : '#B82B22',
            border: 'none',
            '& .MuiChip-label': { px: 1 }
          }
        }
        return {
          height: 22,
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          backgroundColor: isDark
            ? 'rgba(255, 159, 10, 0.20)'
            : 'rgba(255, 149, 0, 0.14)',
          color: isDark ? '#FFB55A' : '#A85700',
          border: 'none',
          '& .MuiChip-label': { px: 1 }
        }
      }}
    />
  )
}

const PlanChip = ({ plan }: { plan?: string }) =>
  plan ? (
    <Chip
      label={plan}
      size="small"
      sx={(t) => ({
        height: 22,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        backgroundColor:
          t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5',
        color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
        border: `1px solid ${
          t.palette.mode === 'dark' ? '#2A2A2E' : '#E5E5EA'
        }`,
        '& .MuiChip-label': { px: 1 }
      })}
    />
  ) : null

const CopyButton = ({
  value,
  label,
  onCopied
}: {
  value: string
  label: string
  onCopied: (label: string) => void
}) => (
  <IconButton
    aria-label={`Copy ${label}`}
    size="small"
    onClick={async () => {
      try {
        await navigator.clipboard.writeText(value)
        onCopied(label)
      } catch {}
    }}
    sx={(t) => ({
      color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
      '&:hover': {
        color: t.palette.mode === 'dark' ? '#FFFFFF' : '#0A0F1C'
      }
    })}
  >
    <ContentCopyIcon sx={{ fontSize: 16 }} />
  </IconButton>
)

const SkeletonSection = () => (
  <Box sx={(t) => sectionSx(t.palette.mode === 'dark')}>
    <Skeleton variant="text" width={100} height={14} sx={{ mb: 2 }} />
    {[0, 1, 2].map((i) => (
      <Stack
        key={i}
        direction="row"
        justifyContent="space-between"
        sx={{ py: 1.25 }}
      >
        <Skeleton variant="text" width={90} />
        <Skeleton variant="text" width={140} />
      </Stack>
    ))}
  </Box>
)

const EmptyState = () => (
  <Box
    sx={(t) => ({
      ...sectionSx(t.palette.mode === 'dark'),
      textAlign: 'center',
      py: { xs: 6, sm: 8 }
    })}
  >
    <PersonSearchIcon
      sx={(t) => ({
        fontSize: 48,
        color: t.palette.mode === 'dark' ? '#36363A' : '#C7C7CC',
        mb: 1.5
      })}
    />
    <Box
      sx={(t) => ({
        fontSize: '1rem',
        fontWeight: 700,
        color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
        mb: 0.5
      })}
    >
      Find a Voxist user
    </Box>
    <Box
      sx={(t) => ({
        fontSize: '0.875rem',
        color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
        maxWidth: { xs: '100%', sm: 360 },
        mx: 'auto'
      })}
    >
      Paste a voxuid, phone number, or email above. We'll figure out which
      one it is.
    </Box>
  </Box>
)

type ModalType = null | 'delete' | 'editPhone' | 'editMail'

const Form = () => {
  const [query, setQuery] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const [searched, setSearched] = useState(false)
  const { setAlert } = useAlert()
  const [modalType, setModalType] = useState<ModalType>(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [selectedPhone, setSelectedPhone] = useState<Phone | null>(null)
  const [selectedMail, setSelectedMail] = useState<Mail | null>(null)
  const [newMail, setNewMail] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [askSupportOpen, setAskSupportOpen] = useState(false)
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecents())

  useEffect(() => {
    try {
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(recents))
    } catch {}
  }, [recents])

  const pushRecent = (q: string, t: DetectedType) => {
    const trimmed = q.trim()
    if (!trimmed) return
    setRecents((prev) => {
      const filtered = prev.filter(
        (r) => r.query.toLowerCase() !== trimmed.toLowerCase()
      )
      return [{ query: trimmed, type: t, ts: Date.now() }, ...filtered].slice(
        0,
        RECENTS_MAX
      )
    })
  }

  const resetSearch = () => {
    setUser(null)
    setSearched(false)
    setQuery('')
  }

  const auth = useAuth()

  const detectedType = useMemo<DetectedType | null>(
    () => (query.trim() ? detectType(query) : null),
    [query]
  )

  const onCopied = (label: string) =>
    setAlert({ type: 'success', text: `${label} copied` })

  const modalClose = () => {
    setSelectedPhone(null)
    setSelectedMail(null)
    setNewMail('')
    setNewPhone('')
    setDeleteConfirm('')
    setModalType(null)
  }

  const send = async (e?: FormEvent, override?: string) => {
    e?.preventDefault()
    const raw = (override !== undefined ? override : query).trim()
    if (!raw) return
    if (override !== undefined) setQuery(override)
    setLoading(true)
    setUser(null)
    setSearched(true)
    const type = detectType(raw)
    pushRecent(raw, type)
    try {
      const token = await auth.getToken()
      const payload: {
        voxuid?: string
        phoneNumber?: string
        email?: string
      } =
        type === 'email'
          ? { email: raw.toLowerCase() }
          : type === 'phone'
          ? { phoneNumber: raw }
          : { voxuid: raw }
      const response = await superagent
        .post(apiUrl('/'))
        .auth(token, { type: 'bearer' })
        .send(payload)
      setUser(response?.body)
    } catch (error: any) {
      setUser(null)
      setAlert({ type: 'error', text: error?.message })
    } finally {
      setLoading(false)
    }
  }

  const sendMail = async () => {
    setActionLoading(true)
    try {
      const token = await auth.getToken()
      const response = await superagent
        .post(apiUrl('/email'))
        .auth(token, { type: 'bearer' })
        .send({
          voxuid: user?.voxuid,
          suid: selectedMail?.suid,
          newMail
        })
      setUser(response?.body)
      modalClose()
    } catch (error: any) {
      setAlert({ type: 'error', text: error?.message })
    } finally {
      setActionLoading(false)
    }
  }

  const sendPhone = async () => {
    setActionLoading(true)
    try {
      const token = await auth.getToken()
      const response = await superagent
        .post(apiUrl('/phone'))
        .auth(token, { type: 'bearer' })
        .send({
          voxuid: user?.voxuid,
          suid: selectedPhone?.suid,
          newPhone
        })
      setUser(response?.body)
      modalClose()
    } catch (error: any) {
      setAlert({ type: 'error', text: error?.message })
    } finally {
      setActionLoading(false)
    }
  }

  const sendDelete = async () => {
    setActionLoading(true)
    try {
      const token = await auth.getToken()
      await superagent
        .delete(apiUrl('/user'))
        .auth(token, { type: 'bearer' })
        .send({ voxuid: user?.voxuid })
      setUser(null)
      setSearched(false)
      setQuery('')
      modalClose()
    } catch (error: any) {
      setAlert({ type: 'error', text: error?.message })
    } finally {
      setActionLoading(false)
    }
  }

  const dialogPaperSx = (t: any) => ({
    borderRadius: { xs: '16px', sm: '20px' },
    backgroundColor: t.palette.mode === 'dark' ? '#0E0E10' : '#FFFFFF',
    backgroundImage: 'none',
    border: `1px solid ${
      t.palette.mode === 'dark' ? '#1F1F22' : '#E5E5EA'
    }`,
    padding: { xs: 0.5, sm: 1 },
    m: { xs: 1.5, sm: 4 },
    width: { xs: 'calc(100% - 24px)', sm: 'auto' }
  })

  const renderModal = () => {
    if (modalType === 'delete') {
      const expected = user?.voxuid || ''
      const canDelete =
        deleteConfirm.trim() === expected && !actionLoading
      return (
        <>
          <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            Delete this account?
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(t) => ({
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                mb: 2
              })}
            >
              This permanently deletes the user's account. This action can't
              be undone. To confirm, type the voxuid below:
            </DialogContentText>
            <Box
              component="code"
              sx={(t) => ({
                display: 'block',
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                p: 1.25,
                mb: 2,
                borderRadius: '8px',
                backgroundColor:
                  t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5',
                color: t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
                wordBreak: 'break-all'
              })}
            >
              {expected}
            </Box>
            <TextField
              fullWidth
              autoFocus
              autoComplete="off"
              label="Type the voxuid to confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={modalClose} sx={secondaryButtonSx}>
              Cancel
            </Button>
            <LoadingButton
              loading={actionLoading}
              onClick={sendDelete}
              disabled={!canDelete}
              sx={destructiveButtonSx}
            >
              Delete account
            </LoadingButton>
          </DialogActions>
        </>
      )
    }
    if (modalType === 'editPhone') {
      return (
        <>
          <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            Replace phone number
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={(t) => ({
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                mb: 2
              })}
            >
              The user will need to log out and back in with the new
              number.
            </DialogContentText>
            <Box
              sx={(t) => ({
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                mb: 0.5
              })}
            >
              Current
            </Box>
            <Box
              component="code"
              sx={(t) => ({
                display: 'block',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                p: 1.25,
                mb: 2,
                borderRadius: '8px',
                backgroundColor:
                  t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5'
              })}
            >
              {selectedPhone?.phone || '—'}
            </Box>
            <TextField
              fullWidth
              autoFocus
              autoComplete="off"
              name="newPhoneNumber"
              label="New phone number"
              value={newPhone}
              onChange={(e) =>
                setNewPhone(e.target.value.replace(/\s+/g, ''))
              }
              placeholder="+33612345678"
              error={newPhone.length > 0 && !isValidPhone(newPhone)}
              helperText={
                newPhone.length > 0 && !isValidPhone(newPhone)
                  ? 'Use international format, digits only (e.g. +33612345678).'
                  : 'International format, no spaces.'
              }
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={modalClose} sx={secondaryButtonSx}>
              Cancel
            </Button>
            <LoadingButton
              loading={actionLoading}
              onClick={sendPhone}
              disabled={!isValidPhone(newPhone)}
              sx={primaryButtonSx}
            >
              Replace
            </LoadingButton>
          </DialogActions>
        </>
      )
    }
    if (modalType === 'editMail') {
      return (
        <>
          <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            Replace email
          </DialogTitle>
          <DialogContent>
            <Box
              sx={(t) => ({
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                mb: 0.5
              })}
            >
              Current
            </Box>
            <Box
              component="code"
              sx={(t) => ({
                display: 'block',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                p: 1.25,
                mb: 2,
                borderRadius: '8px',
                backgroundColor:
                  t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5'
              })}
            >
              {selectedMail?.mail || '—'}
            </Box>
            <TextField
              fullWidth
              autoFocus
              autoComplete="off"
              name="newMail"
              label="New email"
              value={newMail}
              onChange={(e) =>
                setNewMail(e.target.value.replace(/\s+/g, '').toLowerCase())
              }
              placeholder="user@voxist.com"
              error={newMail.length > 0 && !isValidEmail(newMail)}
              helperText={
                newMail.length > 0 && !isValidEmail(newMail)
                  ? 'Enter a valid email, no spaces.'
                  : 'No spaces allowed.'
              }
              sx={inputSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={modalClose} sx={secondaryButtonSx}>
              Cancel
            </Button>
            <LoadingButton
              loading={actionLoading}
              onClick={sendMail}
              disabled={!isValidEmail(newMail)}
              sx={primaryButtonSx}
            >
              Replace
            </LoadingButton>
          </DialogActions>
        </>
      )
    }
    return null
  }

  const detectedLabel: Record<DetectedType, string> = {
    voxuid: 'Voxuid',
    phone: 'Phone',
    email: 'Email'
  }

  return (
    <Box
      sx={(t) => ({
        minHeight: '100vh',
        backgroundColor: t.palette.mode === 'dark' ? '#000000' : '#FBFBFD',
        pt: { xs: 10, sm: 12 },
        pb: 8,
        px: { xs: 2, sm: 4 }
      })}
    >
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: 720, md: 1040, lg: 1160 },
          mx: 'auto'
        }}
      >
        <Box
          sx={(t) => ({
            fontSize: { xs: '1.75rem', sm: '2rem' },
            fontWeight: 900,
            letterSpacing: '-0.02em',
            color: t.palette.mode === 'dark' ? '#FFFFFF' : '#0A0F1C',
            mb: 0.5
          })}
        >
          User lookup
        </Box>
        <Box
          sx={(t) => ({
            fontSize: '0.9375rem',
            color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
            mb: 4
          })}
        >
          Search any Voxist account by voxuid, phone, or email — we'll
          detect which.
        </Box>

        <Box component="form" noValidate onSubmit={send} sx={{ mb: 4 }}>
          <TextField
            fullWidth
            autoFocus
            autoComplete="off"
            placeholder="voxuid-… · +33… · user@voxist.com"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={(t) => {
              const base = inputSx(t)
              return {
                ...base,
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  ...base['& .MuiOutlinedInput-root'],
                  fontSize: '1rem',
                  height: 56
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={(t) => ({
                      color:
                        t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                    })}
                  />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear"
                    size="small"
                    onClick={() => setQuery('')}
                    sx={(t) => ({
                      color:
                        t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                    })}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={(t) => ({
              fontSize: '0.8125rem',
              color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
              minHeight: 44,
              mt: 0.5,
              px: 0.5
            })}
          >
            <Box>
              {detectedType ? (
                <>
                  Will search by{' '}
                  <Box
                    component="span"
                    sx={(t) => ({
                      fontWeight: 700,
                      color:
                        t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C'
                    })}
                  >
                    {detectedLabel[detectedType]}
                  </Box>
                </>
              ) : (
                'Press Enter to search'
              )}
            </Box>
            <LoadingButton
              type="submit"
              loading={loading}
              disabled={!query.trim()}
              sx={(t) => ({
                ...primaryButtonSx(t),
                height: 40,
                px: 3,
                flexShrink: 0
              })}
            >
              Search
            </LoadingButton>
          </Stack>
        </Box>

        {recents.length > 0 && !user && (
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            spacing={1}
            useFlexGap
            sx={{ mb: 4, px: 0.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HistoryIcon
                sx={(t) => ({
                  fontSize: 16,
                  color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                })}
              />
              <Box
                sx={(t) => ({
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                })}
              >
                Recent
              </Box>
            </Stack>
            {recents.map((r) => (
              <Chip
                key={r.query + r.ts}
                label={r.query}
                size="small"
                onClick={() => send(undefined, r.query)}
                sx={(t) => {
                  const isDark = t.palette.mode === 'dark'
                  return {
                    height: 28,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    fontFamily: r.type === 'voxuid' ? 'monospace' : 'inherit',
                    maxWidth: { xs: 160, sm: 240 },
                    backgroundColor: isDark ? '#1C1C1E' : '#F2F2F5',
                    color: isDark ? '#F5F5F7' : '#0A0F1C',
                    border: `1px solid ${isDark ? '#2A2A2E' : '#E5E5EA'}`,
                    cursor: 'pointer',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '&:hover': {
                      backgroundColor: isDark ? '#242427' : '#EFEFF2',
                      borderColor: isDark ? '#36363A' : '#D1D1D6'
                    }
                  }
                }}
              />
            ))}
            <Box
              component="button"
              type="button"
              onClick={() => setRecents([])}
              sx={(t) => ({
                ml: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                fontFamily: 'inherit',
                p: 0.5,
                '&:hover': {
                  color: t.palette.mode === 'dark' ? '#FFFFFF' : '#0A0F1C'
                }
              })}
            >
              Clear
            </Box>
          </Stack>
        )}

        {loading && (
          <Stack spacing={3}>
            <SkeletonSection />
            <SkeletonSection />
          </Stack>
        )}

        {!loading && !user && !searched && <EmptyState />}

        {!loading && !user && searched && (
          <Box sx={(t) => sectionSx(t.palette.mode === 'dark')}>
            <Box
              sx={(t) => ({
                fontSize: '0.9375rem',
                color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                textAlign: 'center',
                py: 2
              })}
            >
              No matching account found.
            </Box>
          </Box>
        )}

        {!loading && user && (
          <Stack spacing={3}>
            <Box>
              <Box
                component="button"
                type="button"
                onClick={resetSearch}
                sx={(t) => {
                  const isDark = t.palette.mode === 'dark'
                  return {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    letterSpacing: '-0.005em',
                    color: isDark ? '#98989D' : '#6E6E73',
                    p: 0.5,
                    borderRadius: '6px',
                    '&:hover': {
                      color: isDark ? '#FFFFFF' : '#0A0F1C'
                    }
                  }
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 12 }} />
                New search
              </Box>
            </Box>
            <Box sx={(t) => sectionSx(t.palette.mode === 'dark')}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background:
                      'linear-gradient(135deg, #00CFF3 0%, #00D7C4 100%)',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    flexShrink: 0
                  }}
                >
                  {initials(user)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    flexWrap="wrap"
                  >
                    <Box
                      sx={(t) => ({
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        letterSpacing: '-0.01em',
                        color:
                          t.palette.mode === 'dark' ? '#FFFFFF' : '#0A0F1C'
                      })}
                    >
                      {user.firstName || user.lastName
                        ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        : 'Unnamed user'}
                    </Box>
                    <PlanChip plan={user.plan} />
                    <RenewChip renew={user.renew} />
                  </Stack>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ mt: 0.25 }}
                  >
                    <Box
                      component="code"
                      sx={(t) => ({
                        fontFamily: 'monospace',
                        fontSize: '0.8125rem',
                        color:
                          t.palette.mode === 'dark'
                            ? '#98989D'
                            : '#6E6E73',
                        wordBreak: 'break-all'
                      })}
                    >
                      {user.voxuid}
                    </Box>
                    <CopyButton
                      value={user.voxuid}
                      label="Voxuid"
                      onCopied={onCopied}
                    />
                  </Stack>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    flexShrink: 0
                  }}
                >
                  <Button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          buildUserSummary(user)
                        )
                        onCopied('Profile')
                      } catch {}
                    }}
                    startIcon={<ContentCopyIcon />}
                    sx={(t) => ({
                      ...secondaryButtonSx(t),
                      height: 40,
                      px: 2,
                      borderRadius: '10px',
                      fontSize: '0.875rem'
                    })}
                  >
                    Copy info
                  </Button>
                  <Button
                    onClick={() => setAskSupportOpen(true)}
                    startIcon={<SupportAgentIcon />}
                    sx={(t) => {
                      const isDark = t.palette.mode === 'dark'
                      return {
                        flexShrink: 0,
                        height: 40,
                        px: 2,
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        letterSpacing: '-0.005em',
                        color: '#FFFFFF',
                        background:
                          'linear-gradient(135deg, #00CFF3 0%, #00D7C4 100%)',
                        boxShadow: isDark
                          ? '0 6px 16px rgba(0, 207, 243, 0.30)'
                          : '0 6px 16px rgba(0, 207, 243, 0.25)',
                        '&:hover': {
                          background:
                            'linear-gradient(135deg, #00B8DB 0%, #00C2B0 100%)',
                          boxShadow: isDark
                            ? '0 8px 20px rgba(0, 207, 243, 0.40)'
                            : '0 8px 20px rgba(0, 207, 243, 0.35)'
                        }
                      }
                    }}
                  >
                    Ask support
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, sm: 3 },
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }
              }}
            >
              <Section
                title="Identity"
                icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
              >
                <Field label="First name" value={user.firstName} />
                <Field label="Last name" value={user.lastName} />
                <Field label="Language" value={user.language} />
              </Section>

              <Section
                title="Subscription"
                icon={<CreditCardOutlinedIcon sx={{ fontSize: 18 }} />}
              >
                <Field label="Plan" value={user.plan} />
                <Field
                  label="Renew"
                  value={
                    user.renew ? (
                      <Stack alignItems="flex-end" spacing={0}>
                        <span>{formatDate(user.renew)}</span>
                        <Box
                          sx={(t) => ({
                            fontSize: '0.75rem',
                            color:
                              t.palette.mode === 'dark'
                                ? '#98989D'
                                : '#6E6E73'
                          })}
                        >
                          {relativeFromNow(user.renew)}
                        </Box>
                      </Stack>
                    ) : (
                      '—'
                    )
                  }
                />
                <Field
                  label="Transcriptions left"
                  value={
                    user.transcriptions === undefined
                      ? '—'
                      : user.transcriptions
                  }
                />
                <Field
                  label="Extra transcriptions"
                  value={
                    user.extraTranscriptions === undefined
                      ? '—'
                      : user.extraTranscriptions
                  }
                />
                <Field
                  label="Created"
                  value={formatDate(user.creationDate)}
                />
              </Section>

              <Section
                title="Phone numbers"
                icon={<PhoneOutlinedIcon sx={{ fontSize: 18 }} />}
                count={(user.phoneNumbers || []).length}
              >
                {(user.phoneNumbers || []).length === 0 && (
                  <Box
                    sx={(t) => ({
                      fontSize: '0.875rem',
                      color:
                        t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                    })}
                  >
                    No phone numbers on file.
                  </Box>
                )}
                {(user.phoneNumbers || []).map((phone) => (
                  <Stack
                    key={phone.suid}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={(t) => ({
                      py: 1.5,
                      borderBottom: `1px solid ${
                        t.palette.mode === 'dark' ? '#1F1F22' : '#F2F2F5'
                      }`,
                      '&:last-of-type': { borderBottom: 'none' }
                    })}
                  >
                    <Box sx={{ flexShrink: 0 }}>
                      <StatusChip active={!!phone.active} />
                    </Box>
                    <Stack
                      spacing={0.25}
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      <Box
                        sx={(t) => ({
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                          fontFamily: 'monospace',
                          color:
                            t.palette.mode === 'dark'
                              ? '#F5F5F7'
                              : '#0A0F1C',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        })}
                      >
                        {phone.phone}
                      </Box>
                      {phone.carrier?.name && (
                        <Box
                          sx={(t) => ({
                            fontSize: '0.75rem',
                            color:
                              t.palette.mode === 'dark'
                                ? '#98989D'
                                : '#6E6E73'
                          })}
                        >
                          {phone.carrier.name}
                        </Box>
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.25}
                      sx={{ flexShrink: 0 }}
                    >
                      <CopyButton
                        value={phone.phone}
                        label="Phone"
                        onCopied={onCopied}
                      />
                      <IconButton
                        aria-label={`Replace ${phone.phone}`}
                        size="small"
                        onClick={() => {
                          setSelectedPhone(phone)
                          setModalType('editPhone')
                        }}
                        sx={(t) => ({
                          color:
                            t.palette.mode === 'dark'
                              ? '#FFFFFF'
                              : '#0A0F1C'
                        })}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
              </Section>

              <Section
                title="Emails"
                icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
                count={(user.mails || []).length}
              >
                {(user.mails || []).length === 0 && (
                  <Box
                    sx={(t) => ({
                      fontSize: '0.875rem',
                      color:
                        t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                    })}
                  >
                    No emails on file.
                  </Box>
                )}
                {(user.mails || []).map((m) => (
                  <Stack
                    key={m.suid}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={(t) => ({
                      py: 1.5,
                      borderBottom: `1px solid ${
                        t.palette.mode === 'dark' ? '#1F1F22' : '#F2F2F5'
                      }`,
                      '&:last-of-type': { borderBottom: 'none' }
                    })}
                  >
                    <Box sx={{ flexShrink: 0 }}>
                      <StatusChip active={!!m.active} />
                    </Box>
                    <Box
                      sx={(t) => ({
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color:
                          t.palette.mode === 'dark'
                            ? '#F5F5F7'
                            : '#0A0F1C',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      })}
                    >
                      {m.mail}
                    </Box>
                    <Stack
                      direction="row"
                      spacing={0.25}
                      sx={{ flexShrink: 0 }}
                    >
                      <CopyButton
                        value={m.mail}
                        label="Email"
                        onCopied={onCopied}
                      />
                      <IconButton
                        aria-label={`Replace ${m.mail}`}
                        size="small"
                        onClick={() => {
                          setSelectedMail(m)
                          setModalType('editMail')
                        }}
                        sx={(t) => ({
                          color:
                            t.palette.mode === 'dark'
                              ? '#FFFFFF'
                              : '#0A0F1C'
                        })}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
              </Section>
            </Box>

            {user.lastVoicemail && (
              <Section
                title="Last voicemail"
                icon={<VoicemailIcon sx={{ fontSize: 18 }} />}
              >
                <Field
                  label="Date"
                  value={formatDate(user.lastVoicemail.date)}
                />
                <Field
                  label="Caller"
                  value={user.lastVoicemail.callerNumber}
                />
                <Field
                  label="vmuid"
                  value={
                    user.lastVoicemail.vmuid ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                      >
                        <Box
                          component="code"
                          sx={(t) => ({
                            fontFamily: 'monospace',
                            fontSize: '0.8125rem',
                            color:
                              t.palette.mode === 'dark'
                                ? '#F5F5F7'
                                : '#0A0F1C'
                          })}
                        >
                          {user.lastVoicemail.vmuid}
                        </Box>
                        <CopyButton
                          value={user.lastVoicemail.vmuid}
                          label="vmuid"
                          onCopied={onCopied}
                        />
                      </Stack>
                    ) : (
                      '—'
                    )
                  }
                />
              </Section>
            )}

            <Box
              sx={(t) => ({
                ...sectionSx(t.palette.mode === 'dark'),
                borderColor:
                  t.palette.mode === 'dark'
                    ? 'rgba(255, 69, 58, 0.30)'
                    : 'rgba(255, 69, 58, 0.25)',
                mt: 1
              })}
            >
              <Box
                sx={(t) => ({
                  ...sectionHeaderSx(t.palette.mode === 'dark'),
                  color: '#FF453A',
                  mb: 1
                })}
              >
                Danger zone
              </Box>
              <Box
                sx={(t) => ({
                  fontSize: '0.875rem',
                  color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73',
                  mb: 2.5
                })}
              >
                Deleting this account is permanent. The user will lose all
                voicemails, transcriptions and history.
              </Box>
              <Button
                fullWidth
                onClick={() => setModalType('delete')}
                sx={destructiveButtonSx}
              >
                Delete account
              </Button>
            </Box>
          </Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(modalType)}
        onClose={modalClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        {renderModal()}
      </Dialog>

      <AskSupportDialog
        open={askSupportOpen}
        onClose={() => setAskSupportOpen(false)}
        user={user}
      />
    </Box>
  )
}

export default Form
